import { EMPTY_STRING, KeyNames } from "@regrello/core-utils";
import type { FieldInstanceFields } from "@regrello/graphql-api";
import { type RegrelloDataGridV2EditableCellProps, useDataGridV2EditableCell } from "@regrello/ui-core";
import { useCallback, useRef } from "react";
import { isWithinElement } from "../../../../../../utils/isWithinElement";
import type { CustomFieldFrontendSelectableOption } from "../../../customFields/plugins/types/CustomFieldFrontendSelectableOption";
import { extractAllowedValuesFromFieldVersionForFrontend } from "../../../customFields/plugins/utils/extractAllowedValuesForFrontend";
import { RegrelloFormFieldSelect } from "../../../formFields/RegrelloFormFieldSelect";

export interface RegrelloEditableCellSelectProps {
  /** The field instance that corresponds to the current table column. */
  fieldInstance: FieldInstanceFields;

  /** Callback when the user changes the value of the cell. */
  onValueChange: (value: CustomFieldFrontendSelectableOption | null) => void;

  /** Current cell value before editing; reinstated if editing is canceled. */
  value: CustomFieldFrontendSelectableOption | null;
}

/**
 * Editable single-select cell in which the user picks one option from allowed values.
 */
export function RegrelloEditableCellSelect({
  api,
  eventKey,
  fieldInstance,
  value,
  onValueChange,
}: RegrelloDataGridV2EditableCellProps<RegrelloEditableCellSelectProps>) {
  const editableCellRef = useRef<HTMLDivElement>(null);
  const initialInputValue = eventKey != null && eventKey !== KeyNames.ENTER ? eventKey : EMPTY_STRING;

  const { columnWidth, onKeyDownCapture } = useDataGridV2EditableCell({
    api,
    eventKey,
    // These keys are used for navigating the menu; let the input handle them
    suppressKeyArrowDown: true,
    suppressKeyArrowUp: true,
  });

  const items: CustomFieldFrontendSelectableOption[] = extractAllowedValuesFromFieldVersionForFrontend(
    fieldInstance.spectrumFieldVersion,
  );

  // (jullman): When the user closes the select menu via selecting an option, close the cell and
  // highlight the cell that was just edited. If the cell was closed via Enter (or other key that
  // has actions determined by onKeyDownCapture), the microtask will correct selected cell before
  // the first paint (see onKeyDown).
  const onClose = useCallback(() => {
    api.stopEditing();
    const row = api.getFocusedCell()?.rowIndex;
    const columnId = api.getFocusedCell()?.column;
    if (row == null || columnId == null) {
      console.warn("No editing cell found");
      return;
    }
    api.setFocusedCell(row, columnId);
  }, [api]);

  // (jullman): queue onKeyDownCapture as a microtask to allow for RegrelloFormFieldSelect to handle
  // selection on Enter first before we set the new focused cell.
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      queueMicrotask(() => {
        onKeyDownCapture(event);
      });
    },
    [onKeyDownCapture],
  );

  return (
    <div ref={editableCellRef} style={{ width: columnWidth }}>
      <RegrelloFormFieldSelect
        autoFocus={true}
        getOptionLabel={(option) => option.displayValue}
        getOptionValue={(option) => option.displayValue}
        highlightButtonInOpenState={true}
        inputProps={{
          onKeyDown: onKeyDown,
          value: initialInputValue,
        }}
        isDefaultMarginsOmitted={true}
        onBlur={(event) => {
          const isWithinInput = isWithinElement(
            event.relatedTarget as HTMLElement | null | undefined,
            editableCellRef.current,
          );
          if (!isWithinInput) {
            api.stopEditing();
          }
        }}
        onChange={(next) => {
          onValueChange(next);
        }}
        onClose={onClose}
        openOnMount={true}
        options={items}
        renderSelectedValue={(option) => option?.displayValue}
        size="x-small"
        suppressHelperTextBelowFieldInput={true}
        suppressSelectValueOnOpen={true}
        value={value}
      />
    </div>
  );
}
